if (!customElements.get('slideshow-list')) {
  class SlideshowList extends HTMLElement {
    static SLIDE_CLASS = 'slideshow-list-swiper__slide';
    static MOBILE_MQ = '(max-width: 767px)';

    connectedCallback() {
      // Wait one frame so sibling controls exist in the DOM.
      requestAnimationFrame(() => this.setup());
    }

    disconnectedCallback() {
      this.mediaQuery?.removeEventListener('change', this.onBreakpointChange);
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.destroySwiper();
    }

    setup() {
      this.applyLayoutVars();
      this.observeReveal();

      if (this.dataset.stackMobile === 'true') {
        this.mediaQuery = window.matchMedia(SlideshowList.MOBILE_MQ);
        this.onBreakpointChange = () => this.syncSwiperMode();
        this.mediaQuery.addEventListener('change', this.onBreakpointChange);
        this.syncSwiperMode();
        return;
      }

      this.initSwiper();
    }

    /**
     * Self-healing visibility handling: a ResizeObserver fires whenever this
     * element's width changes — including display:none -> visible (0 -> N),
     * which covers tabs, drawers and accordions no matter how fast they
     * toggle. Swiper is never inited/updated while hidden (see refresh()),
     * so this is the single reliable recovery point on reveal.
     */
    observeReveal() {
      if (this.resizeObserver) return;

      this._lastWidth = this.offsetWidth;

      this.resizeObserver = new ResizeObserver((entries) => {
        const width = entries[entries.length - 1].contentRect.width;
        if (width === this._lastWidth) return;
        this._lastWidth = width;
        if (width > 0) this.refresh();
      });

      this.resizeObserver.observe(this);
    }

    parseSlidesPerView(value, fallback) {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    }

    isFractional(value) {
      return Number.isFinite(value) && !Number.isInteger(value);
    }

    shouldStack() {
      return this.dataset.stackMobile === 'true' && this.mediaQuery?.matches;
    }

    /** True while inside a hidden ancestor (inactive tab, drawer, etc.). */
    isHiddenFromLayout() {
      return this.offsetWidth === 0 && this.offsetHeight === 0;
    }

    getCarouselRoot() {
      return this.closest('[data-carousel-root]') || this.parentElement || this;
    }

    queryControl(selector) {
      const root = this.getCarouselRoot();
      if (!root) return null;

      for (const candidate of root.querySelectorAll(selector)) {
        const owner = candidate.closest('slideshow-list');
        if (!owner || owner === this) return candidate;
      }

      return null;
    }

    applyLayoutVars() {
      const desktopCards = this.parseSlidesPerView(this.dataset.desktopCards, 4);
      const mobileCards = this.parseSlidesPerView(this.dataset.mobileCards, 2);
      const gapDesktop = parseFloat(this.dataset.gap);
      const gapMobile = parseFloat(this.dataset.gapMobile);
      const spaceBetweenDesktop = Number.isFinite(gapDesktop) ? gapDesktop : 30;
      const spaceBetweenMobile = Number.isFinite(gapMobile) ? gapMobile : spaceBetweenDesktop;
      const rowGapDesktop = parseFloat(this.dataset.rowGap);
      const rowGapMobile = parseFloat(this.dataset.rowGapMobile);
      const bleedEnabled = this.dataset.bleed !== 'false';

      this._layout = {
        desktopCards,
        mobileCards,
        spaceBetweenDesktop,
        spaceBetweenMobile,
      };

      this.style.setProperty('--visible-cards-mobile', String(mobileCards));
      this.style.setProperty('--visible-cards-desktop', String(desktopCards));
      this.style.setProperty('--card-gap', `${spaceBetweenDesktop}px`);
      this.style.setProperty('--card-gap-mobile', `${spaceBetweenMobile}px`);
      this.style.setProperty('--card-row-gap', `${Number.isFinite(rowGapDesktop) ? rowGapDesktop : spaceBetweenDesktop}px`);
      this.style.setProperty('--card-row-gap-mobile', `${Number.isFinite(rowGapMobile) ? rowGapMobile : spaceBetweenMobile}px`);
      this.style.setProperty('--stack-columns', String(Math.max(1, Math.round(mobileCards))));

      this.classList.toggle('is-bleed-mobile', bleedEnabled && this.isFractional(mobileCards));
      this.classList.toggle('is-bleed-desktop', bleedEnabled && this.isFractional(desktopCards));
    }

    /**
     * Public API for tabs / drawers:
     * init if needed, otherwise recalculate sizes after becoming visible.
     * Do not destroy on hide — keep the instance and refresh on show.
     */
    refresh() {
      // Never init/update while hidden (zero-size measurements corrupt
      // Swiper). The ResizeObserver re-invokes refresh() on reveal.
      if (this.isHiddenFromLayout()) return;

      if (this.shouldStack()) {
        this.syncSwiperMode();
        return;
      }

      if (this.swiper && !this.swiper.destroyed) {
        this.swiper.update();
        this.swiper.scrollbar?.updateSize();
        this.swiper.navigation?.update();
        this.swiper.pagination?.render();
        this.swiper.pagination?.update();
        return;
      }

      this.initSwiper();
    }

    syncSwiperMode() {
      this.applyLayoutVars();

      if (this.shouldStack()) {
        this.destroySwiper();
        this.classList.add('is-stacked', 'is-ready');
        this.dispatchReady(null);
        return;
      }

      this.classList.remove('is-stacked');
      if (!this.swiper) this.initSwiper();
    }

    destroySwiper() {
      if (this._autoplayHoverCleanup) {
        this._autoplayHoverCleanup();
        this._autoplayHoverCleanup = null;
      }
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
      this.classList.remove('is-smooth-autoplay');
    }

    initSwiper() {
      const swiperContainer = this.querySelector('.slideshow-list-swiper');
      if (!swiperContainer || this.swiper) {
        this.classList.add('is-ready');
        return;
      }

      // Hidden (e.g. inactive tab): skip now, the ResizeObserver set up in
      // observeReveal() calls refresh() as soon as the element gets a size.
      if (this.isHiddenFromLayout()) return;

      if (!this._layout) this.applyLayoutVars();

      const { desktopCards, mobileCards, spaceBetweenDesktop, spaceBetweenMobile } = this._layout;

      const prevEl = this.queryControl('[data-swiper-prev]');
      const nextEl = this.queryControl('[data-swiper-next]');
      const scrollbarEl = this.queryControl('carousel-scrollbar, [data-swiper-scrollbar]');
      const paginationEl = this.queryControl('carousel-pagination, [data-swiper-pagination]');

      const enableNavigation =
        this.dataset.navigation !== 'false' && Boolean(prevEl && nextEl);
      const enableScrollbar =
        this.dataset.scrollbar !== 'false' && Boolean(scrollbarEl);
      const enablePagination =
        this.dataset.pagination !== 'false' && Boolean(paginationEl) && !enableScrollbar;
      const enableAutoplay = this.dataset.autoplay === 'true';
      const smoothAutoplay = enableAutoplay && this.dataset.autoplaySmooth === 'true';
      const autoplayDelaySeconds = parseFloat(this.dataset.autoplaySpeed);
      const autoplayDelay =
        Number.isFinite(autoplayDelaySeconds) && autoplayDelaySeconds > 0
          ? autoplayDelaySeconds * 1000
          : 5000;
      const pauseOnHover = this.dataset.autoplayPauseHover !== 'false';
      const enableLoop = this.dataset.loop === 'true' || smoothAutoplay;
      const enableMousewheel = this.dataset.mousewheel !== 'false';

      this.classList.toggle('is-smooth-autoplay', smoothAutoplay);

      const config = {
        wrapperClass: 'slideshow-list-swiper__wrapper',
        slideClass: SlideshowList.SLIDE_CLASS,
        slidesPerView: mobileCards,
        spaceBetween: spaceBetweenMobile,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        grabCursor: this.dataset.grab !== 'false',
        loop: enableLoop,
        freeMode: smoothAutoplay,
        speed: smoothAutoplay ? autoplayDelay : 300,
        // Mousewheel is pointer-only; keyboard + a11y cover keyboard / SR users.
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        a11y: {
          enabled: true,
          containerMessage: this.getAttribute('aria-label') || 'Carousel',
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
          firstSlideMessage: 'This is the first slide',
          lastSlideMessage: 'This is the last slide',
          paginationBulletMessage: 'Go to slide {{index}}',
          slideLabelMessage: '{{index}} / {{slidesLength}}',
        },
        breakpoints: {
          768: {
            slidesPerView: desktopCards,
            spaceBetween: spaceBetweenDesktop,
          },
        },
        on: {
          init: (swiper) => {
            this.swiper = swiper;
            this.classList.add('is-ready');
            this.ensureCarouselSemantics(swiperContainer);
            this.dispatchReady(swiper);
          },
        },
      };

      if (enableMousewheel) {
        config.mousewheel = {
          enabled: true,
          forceToAxis: true,
          releaseOnEdges: true,
        };
      }

      if (enableNavigation) {
        config.navigation = { prevEl, nextEl };
      }

      if (enableScrollbar) {
        config.scrollbar = {
          el: scrollbarEl,
          draggable: true,
          dragClass: 'slideshow-list-swiper__scrollbar-drag',
        };
      }

      if (enablePagination) {
        config.pagination = {
          el: paginationEl,
          clickable: true,
          type: 'bullets',
          bulletClass: 'slideshow-list-swiper__bullet',
          bulletActiveClass: 'slideshow-list-swiper__bullet--active',
        };
      }

      if (enableAutoplay) {
        config.autoplay = {
          enabled: true,
          delay: smoothAutoplay ? 0 : autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: false,
        };
      }

      // Constructor fires `init` synchronously and assigns this.swiper there.
      new Swiper(swiperContainer, config);

      // Hover pause/resume — freeze translate so smooth/continuous scroll actually stops.
      if (enableAutoplay && pauseOnHover && this.swiper) {
        const hoverTarget = this.swiper.el || swiperContainer;
        const resumeSpeed = smoothAutoplay ? autoplayDelay : 300;

        const stopAutoplay = () => {
          if (!this.swiper?.autoplay) return;
          const swiperTranslate = this.swiper.getTranslate();
          this.swiper.setTransition(0);
          this.swiper.setTranslate(swiperTranslate);
          this.swiper.autoplay.stop();
        };

        const startAutoplay = () => {
          if (!this.swiper?.autoplay) return;
          this.swiper.slideTo(this.swiper.activeIndex, resumeSpeed, false);
          this.swiper.autoplay.start();
        };

        hoverTarget.addEventListener('mouseenter', stopAutoplay);
        hoverTarget.addEventListener('mouseleave', startAutoplay);

        this._autoplayHoverCleanup = () => {
          hoverTarget.removeEventListener('mouseenter', stopAutoplay);
          hoverTarget.removeEventListener('mouseleave', startAutoplay);
        };
      }
    }

    dispatchReady(swiper) {
      this.dispatchEvent(
        new CustomEvent('slideshow-list:ready', {
          bubbles: true,
          detail: { swiper, slideshow: this },
        })
      );
    }

    /**
     * Ensure carousel landmarks exist for keyboard / screen-reader users.
     * Mousewheel is a pointer enhancement; nav buttons + keyboard arrows are the accessible path.
     */
    ensureCarouselSemantics(container) {
      if (!container) return;

      if (!container.getAttribute('role')) {
        container.setAttribute('role', 'region');
      }
      if (!container.getAttribute('aria-roledescription')) {
        container.setAttribute('aria-roledescription', 'carousel');
      }
      if (!container.getAttribute('aria-label') && !this.getAttribute('aria-label')) {
        container.setAttribute('aria-label', 'Carousel');
      } else if (!container.getAttribute('aria-label') && this.getAttribute('aria-label')) {
        container.setAttribute('aria-label', this.getAttribute('aria-label'));
      }

      if (!container.hasAttribute('tabindex')) {
        container.setAttribute('tabindex', '0');
      }
    }
  }

  customElements.define('slideshow-list', SlideshowList);
}
